import { useState } from "react";
import { Shield, WarningCircle, SignIn } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { login, passwordLogin } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const authError = params.get("auth_error");
  const authReason = params.get("auth_reason");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await passwordLogin(username, password);
    } catch (err) {
      setError(err.message);
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E7C86] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] p-8 flex flex-col items-center gap-6">
        <div className="w-14 h-14 rounded-xl bg-[#0E7C86]/10 flex items-center justify-center">
          <Shield size={28} weight="fill" className="text-[#0E7C86]" />
        </div>
        <div className="text-center">
          <h1 className="font-heading text-xl font-bold text-[#101828]">Production Control</h1>
          <p className="text-sm text-[#667085] mt-1">Sign in with your organization account to continue</p>
        </div>
        <Button
          onClick={login}
          className="w-full h-11 bg-[#0E7C86] hover:bg-[#0B6B74] text-white font-bold rounded-lg gap-2.5"
          data-testid="microsoft-login-button"
        >
          <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          Continue with Microsoft
        </Button>

        <div className="w-full flex items-center gap-3">
          <span className="h-px flex-1 bg-[#EAECF0]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#98A2B3]">or shop floor login</span>
          <span className="h-px flex-1 bg-[#EAECF0]" />
        </div>

        {/* Oct 26 2026: operators and supervisors have no company mailbox,
            so they sign in with a username an admin gave them. */}
        <form className="w-full space-y-2" onSubmit={submit} data-testid="floor-login-form">
          <Input
            className="h-11"
            placeholder="Username"
            autoCapitalize="none"
            autoCorrect="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            data-testid="floor-login-username"
          />
          <Input
            className="h-11"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="floor-login-password"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={busy || !username || !password}
            className="w-full h-11 font-bold rounded-lg gap-2"
            data-testid="floor-login-submit"
          >
            <SignIn size={16} weight="bold" />
            {busy ? "Signing in..." : "Sign in"}
          </Button>
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="floor-login-error">
              {error}
            </p>
          )}
        </form>

        <p className="text-xs text-[#98A2B3] text-center">
          Access is managed by your IT administrator. Contact them if you're unable to sign in.
        </p>
        {authError && (
          <div
            className="w-full flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700"
            data-testid="login-error-banner"
          >
            <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
            <span>{authReason ? decodeURIComponent(authReason) : "Sign-in failed. Please try again."}</span>
          </div>
        )}
      </div>
    </div>
  );
}
